import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { DotCMSClient } from '@dotcms/angular';
import { createDotCMSClient } from '@dotcms/client';
import {
  DotCMSClientConfig,
  DotCMSComposedPageAsset,
  DotCMSComposedPageResponse,
  DotCMSExtendedPageResponse,
  DotCMSPageRequestParams,
  DotErrorPage,
} from '@dotcms/types';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, defer, from, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, retry, switchMap, tap } from 'rxjs/operators';
import { ConfigService } from 'src/app/extensions/dot-cms-uve/services/dot-cms-uve-config/dot-cms-config.service';

import { getCurrentDotCmsApiConfiguration } from 'ish-core/store/core/configuration';
import { DomService } from 'ish-core/utils/dom/dom.service';

import { buildPageQuery, buildQuery } from '../../utils/built-query-utils';

/** how often the browser asks dotCMS again for a page when the connection failed */
const DOT_CMS_PAGE_RETRIES = 2;

/**
 * CUSTOMIZATION: tells a failed connection from an answer.
 *
 * `status` 0 is the SDK's "Network error: fetch failed" (no connection, timeout); 408, 429 and 502-504 are a
 * gateway or dotCMS being busy. All of them can succeed on the next attempt. Anything else (404, a permission
 * error, a GraphQL error) is dotCMS answering, and asking again returns the same.
 */
export function isTransientDotCmsError(error: unknown): boolean {
  const status = (error as DotErrorPage)?.status ?? (error as DotErrorPage)?.httpError?.status;
  return [0, 408, 429, 502, 503, 504].includes(status);
}

@Injectable({ providedIn: 'root' })
export class DotCmsUvePageService {
  private configService = inject(ConfigService);
  private httpClient = inject(HttpClient);
  private store = inject(Store);
  private domService = inject(DomService);
  private document = inject<Document>(DOCUMENT);

  private clientSig = signal<DotCMSClient | null>(null);

  constructor() {
    this.configService
      .getConfig$()
      .pipe(
        filter((config): config is DotCMSClientConfig => !!config),
        tap(config => this.setCSS(config)),
        map(config => ({
          ...config,
        }))
      )
      .subscribe({
        next: config => {
          const client = createDotCMSClient(config);
          this.clientSig.set(client);
        },
        error: err => console.error('Config stream error', err),
        complete: () => console.debug('Config stream completed'),
      });
  }
  setCSS(config: DotCMSClientConfig): void {
    if (!SSR) {
      const link = this.domService.createElement<HTMLLinkElement>('link', this.document.head);
      this.domService.setProperty(link, 'rel', 'stylesheet');
      this.domService.setProperty(link, 'type', 'text/css');
      this.domService.setProperty(
        link,
        'href',
        `${config.dotcmsUrl}/application/styles.dotsass?host_id=${config.siteId}`
      );
    }
  }

  /**
   * Get the page and navigation for the given url and params.
   */
  getPageAsset<T extends DotCMSExtendedPageResponse>(
    url: string,
    params: DotCMSPageRequestParams
  ): Observable<DotCMSComposedPageResponse<T>> {
    return this.configService.getConfig$().pipe(
      filter((config): config is DotCMSClientConfig => !!config),
      switchMap(config =>
        // `defer`, so that `retry` sends the request again instead of re-reading the settled promise
        defer(() =>
          from(
            this.clientSig().page.get<T>(url, {
              ...params,
            })
          )
        ).pipe(
          // CUSTOMIZATION: one failed connection used to end on the "Page not found!" error page. The browser
          // now asks again first. The server does not: a connection timeout already costs it 10s, and the
          // browser repeats the request anyway (see DotCmsUveEditablePageService).
          retry({
            count: SSR ? 0 : DOT_CMS_PAGE_RETRIES,
            delay: (error, attempt) => (isTransientDotCmsError(error) ? timer(attempt * 500) : throwError(() => error)),
          }),
          catchError((error: DotErrorPage) => {
            console.error('DotCMS error', error);

            // `client.page.get()` REJECTS WITH AN `Error`, NOT A RESPONSE.
            //
            // It throws `DotErrorPage extends Error` (fields: message, status, code, httpError,
            // graphql). Everything downstream tests `resp?.error`, which is `undefined` on an
            // Error — so a FAILED page used to read as a SUCCESSFUL empty one. Outside the editor
            // `#setPageContent` stored it with status 'success' and no `pageAsset`, and the
            // storefront sat on a loading spinner instead of routing to the error page; inside
            // the editor it fell straight through to `listen()`. Converting the rejection into
            // the `{ error }` shape the callers already expect is what makes the failure
            // detectable at all.
            //
            // `graphql` is carried too, because the EDITOR can still recover from this: it
            // re-runs the query under the author's own session, where a page that has no LIVE
            // version (saved but never published) resolves under EDIT_MODE. `DotErrorPage`
            // already carries the exact document that was sent, so prefer it and only rebuild
            // one if some non-SDK error arrives here.
            return of({
              error,
              graphql: error?.graphql?.query ? error.graphql : this.buildEditorPayload(url, params, config),
            } as unknown as DotCMSComposedPageResponse<T>);
          })
        )
      )
    );
  }

  /**
   * The `{ query, variables }` the UVE re-runs, rebuilt for a request that failed.
   *
   * Uses the project's own `buildPageQuery` — the same document `getBlogDetailPageAsset` ships,
   * which declares `$mode` and passes `pageMode: $mode`, so the editor's override lands on a
   * declared variable. It also selects `canRead`, `canEdit` and `viewAs`, which the editor shell
   * reads off its OWN re-run and blanks the canvas without.
   */
  private buildEditorPayload(
    url: string,
    params: DotCMSPageRequestParams,
    config: DotCMSClientConfig
  ): { query: string; variables: Record<string, unknown> } {
    return {
      query: buildPageQuery({
        page: params.graphql?.page,
        fragments: params.graphql?.fragments,
        additionalQueries: buildQuery(params.graphql?.content),
      }),
      variables: {
        url,
        // Ours is LIVE; the editor replaces this key on its re-run. See `getPageRequestMode()`.
        mode: 'LIVE',
        ...(params.languageId ? { languageId: String(params.languageId) } : {}),
        ...(params.personaId ? { personaId: params.personaId } : {}),
        ...(config.siteId ? { siteId: config.siteId } : {}),
      },
    };
  }
  getBlogDetailPageAsset(
    url: string,
    params: DotCMSPageRequestParams
  ): Observable<DotCMSComposedPageResponse<unknown>> {
    const contentQuery = buildQuery(params.graphql.content);
    const pageQuery = buildPageQuery({
      page: params.graphql.page,
      fragments: params.graphql.fragments,
      additionalQueries: contentQuery,
    });
    return combineLatest([this.store.pipe(select(getCurrentDotCmsApiConfiguration))]).pipe(
      map(([config]) => config),
      filter(config => config !== undefined),
      switchMap(config => {
        // Held in a named const so the SAME object can ride along on the response below. The UVE
        // re-runs this query and merges its own page params over these variables, so shipping
        // `undefined` left the editor with nothing to override.
        const variables = {
          url,
          mode: 'LIVE',
          languageId: params.languageId,
          fireRules: false,
          siteId: config.hostId,
        };
        const headers = new HttpHeaders({
          Authorization: `Bearer ${config.token}`,
          'Content-Type': 'application/json',
        });

        return this.httpClient
          .post(`${config.host}/api/v1/graphql`, { query: pageQuery, variables }, { headers })
          .pipe(map(pageResponse => ({ pageResponse, variables })));
      }),
      map(({ pageResponse, variables }) => {
        //@ts-ignore
        const { footer, navigation, page } = pageResponse.data;
        const { urlContentMap } = page;
        const { _map, author, blogComment, blogContent, imageBlog } = urlContentMap;
        const mapTest = {
          ..._map,
          author,
          blogComment,
          blogContent,
          imageBlog,
        };
        const pageTest = {
          ...page,
          urlContentMap: mapTest,
        };
        return {
          pageAsset: pageTest as DotCMSComposedPageAsset<DotCMSExtendedPageResponse>,
          content: {
            navigation,
            footer,
          },
          // `DotCMSComposedPageResponse` requires the list, its own contract is to check `errors.length`
          // and not whether it is there at all
          errors: [],
          graphql: { query: pageQuery, variables },
        };
      })
    );
  }
}
